import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { UserAvatar } from "@/components/ui/user-avatar";
import { ChatThreadRow, type ChatRowLabels } from "@/components/chat/thread/ChatThreadRow";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import type { ProjectMessage } from "@/domains/messages/types";
import { messageAuthorName } from "@/domains/messages/authorName";
import { filterExpiredTombstones, groupMessages } from "./grouping";
import { ReactionBar } from "./ReactionBar";

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dividerLabel(iso: string, today: string, yesterday: string, language: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (isSameDay(d, now)) return today;
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (isSameDay(d, y)) return yesterday;
  // "6 Jul 2026" (en) / "6. juul 2026" (et) — matches the dashboard redesign.
  return d.toLocaleDateString(language === "et" ? "et-EE" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Time of day — the feed's day dividers carry the date. */
function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** A soft-deleted message keeps the author's avatar so it stays clear whose it
 *  was. Tombstones older than TOMBSTONE_TTL_MS are filtered out before this. */
function MessageTombstone({ message, note }: { message: ProjectMessage; note: string }) {
  return (
    <div data-message-id={message.id} className="flex gap-2">
      <UserAvatar user={message.author} size={26} className="mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1 py-0.5">
        <span className="text-xs italic text-muted-foreground">{note}</span>
      </div>
    </div>
  );
}

/** The quoted parent above a reply — click jumps to the referenced message.
 *  A light grey block, so it stays visible on the white dashboard card and
 *  the modal alike. */
function ReplyQuote({
  message,
  onQuoteClick,
  deletedLabel,
}: {
  message: ProjectMessage;
  onQuoteClick: (messageId: number) => void;
  deletedLabel: string;
}) {
  const parent = message.reply_to;
  if (!parent) return null;
  if (parent.deleted) {
    return (
      <div className="ml-2 mt-1 rounded-md border-l-[3px] border-neutral-400 bg-neutral-200/60 px-2.5 py-1.5 text-xs italic text-muted-foreground">
        {deletedLabel}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onQuoteClick(parent.id)}
      className="ml-2 mt-1 block max-w-full rounded-md border-l-[3px] border-neutral-400 bg-neutral-200/60 px-2.5 py-1.5 text-left transition-colors hover:bg-neutral-200"
    >
      <span className="block truncate text-[11px] font-medium text-muted-foreground">
        {parent.authorName}
      </span>
      <span className="block truncate text-xs text-muted-foreground">{parent.body}</span>
    </button>
  );
}

interface MessageListProps {
  messages: ProjectMessage[];
  currentUserId: number | undefined;
  buildingId: number;
  buildingProjectId: number;
  mentionDisplayNames: string[];
  deriveMentionIds: (text: string) => number[];
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  onReply: (message: ProjectMessage) => void;
  onReact: (messageId: number, emoji: string) => void;
  onEdit: (messageId: number, body: string, mentionedUserIds: number[]) => void;
  onDelete: (messageId: number) => void;
  /** Jump to + highlight a message (from a reply-quote click). */
  onQuoteClick: (messageId: number) => void;
  /** Id of the message currently pulsing after a jump. */
  highlightId: number | null;
}

/** The grouped, divider-separated feed body. Grouping is pure (see grouping.ts). */
export function MessageList({
  messages,
  currentUserId,
  buildingId,
  buildingProjectId,
  mentionDisplayNames,
  deriveMentionIds,
  openViewer,
  onReply,
  onReact,
  onEdit,
  onDelete,
  onQuoteClick,
  highlightId,
}: MessageListProps) {
  const { t, language } = useLanguage();
  // Which message is being edited, and its in-progress text — held here, as
  // the shared thread row is a controlled component.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState("");

  // Tombstones expire TOMBSTONE_TTL_MS after deletion. A slow tick keeps
  // `now` fresh so they disappear live (within a minute of the deadline)
  // without waiting for a refetch; the interval only runs while the list
  // actually contains a tombstone.
  const [now, setNow] = useState(() => Date.now());
  const hasTombstones = messages.some((m) => m.deleted_at != null);
  useEffect(() => {
    if (!hasTombstones) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [hasTombstones]);

  const groups = useMemo(
    () => groupMessages(filterExpiredTombstones(messages, now)),
    [messages, now]
  );

  const rowLabels: ChatRowLabels = {
    reply: t.messages.reply,
    edit: t.messages.edit,
    edited: t.messages.edited,
    save: t.pm.common.save,
    cancel: t.pm.common.cancel,
    delete: t.messages.delete,
    confirmDelete: t.messages.confirmDelete,
    confirmDeleteDescription: t.messages.confirmDeleteDescription,
  };

  const saveEdit = (message: ProjectMessage) => {
    const trimmed = editBody.trim();
    if (!trimmed) return;
    if (trimmed !== message.body) onEdit(message.id, trimmed, deriveMentionIds(trimmed));
    setEditingId(null);
    setEditBody("");
  };

  return (
    <div className="flex flex-col gap-1">
      {groups.map((group) => (
        <div key={group.dayKey}>
          {/* Small centred grey date label — no flanking rules, per the flat
              dashboard redesign. */}
          <div className="my-2 text-center text-[11px] font-medium text-muted-foreground">
            {dividerLabel(group.dayTimestamp, t.messages.today, t.messages.yesterday, language)}
          </div>
          {group.items.map(({ message, showHeader }) =>
            message.deleted_at ? (
              <MessageTombstone key={message.id} message={message} note={t.messages.deletedNote} />
            ) : (
              <ChatThreadRow
                key={message.id}
                anchorId={message.id}
                author={message.author}
                authorName={messageAuthorName(message)}
                timestamp={formatTime(message.created_at)}
                edited={Boolean(message.modified_at)}
                body={message.body}
                mentionDisplayNames={mentionDisplayNames}
                attachments={message.linked_documents}
                buildingId={buildingId}
                projectId={buildingProjectId}
                openViewer={openViewer}
                labels={rowLabels}
                showHeader={showHeader}
                highlighted={highlightId === message.id}
                quote={
                  <ReplyQuote
                    message={message}
                    onQuoteClick={onQuoteClick}
                    deletedLabel={t.messages.deletedQuote}
                  />
                }
                footer={
                  <ReactionBar
                    reactions={message.reactions}
                    onToggle={(emoji) => onReact(message.id, emoji)}
                  />
                }
                canEdit={currentUserId != null && message.author_id === currentUserId}
                isEditing={editingId === message.id}
                editBody={editBody}
                onEditBodyChange={setEditBody}
                onStartEdit={() => {
                  setEditingId(message.id);
                  setEditBody(message.body);
                }}
                onCancelEdit={() => {
                  setEditingId(null);
                  setEditBody("");
                }}
                onSaveEdit={() => saveEdit(message)}
                onReply={() => onReply(message)}
                onDelete={
                  currentUserId != null && message.author_id === currentUserId
                    ? () => onDelete(message.id)
                    : undefined
                }
              />
            )
          )}
        </div>
      ))}
    </div>
  );
}
